import { useEffect, useState } from "react";
import Modal from "../modals/Modal";
import { useToast } from "../../contexts/ToastContext";
import { KIOSK_APP_URL, KIOSK_APP_HOST } from "../../config/kioskApp";

// Format a 6-digit code as "428 193".
const formatCode = (code) => (code ? `${code.slice(0, 3)} ${code.slice(3)}` : "");

const mmss = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

/**
 * PairingCodeModal — reveals a one-time pairing code with an expiry countdown.
 * The code is shown ONCE (the server stores only its hash); closing it means a
 * new code must be generated via Re-pair.
 */
const PairingCodeModal = ({ kioskName, code, expiresAt, onClose }) => {
  const toast = useToast();
  const [remaining, setRemaining] = useState(() => Math.max(0, (expiresAt || 0) - Date.now()));

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(Math.max(0, (expiresAt || 0) - Date.now()));
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const expired = remaining <= 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied.");
    } catch {
      toast.error("Could not copy. Read it from the screen.");
    }
  };

  return (
    <Modal isOpen onClose={onClose} labelledBy="kiosk-code-title" className="kiosk-modal">
        <div className="kiosk-modal-header">
          <h2 id="kiosk-code-title" className="kiosk-modal-title">
            Pairing code — {kioskName}
          </h2>
          <div className="kiosk-modal-sub">
            Enter this on the kiosk device to pair it. Shown only once.
          </div>
        </div>
        <div className="kiosk-modal-body">
          <div className="kiosk-code-reveal">
            <div className="kiosk-code-digits">{formatCode(code)}</div>
            <div className={`kiosk-code-expiry ${expired ? "expired" : ""}`}>
              {expired ? "Code expired — generate a new one" : <>Expires in {mmss(remaining)}</>}
            </div>
          </div>
          <div className="kiosk-code-steps">
            <ol>
              <li>Open <a href={KIOSK_APP_URL} target="_blank" rel="noopener noreferrer"><strong>{KIOSK_APP_HOST}</strong></a> on the device.</li>
              <li>Enter the 6-digit code above.</li>
              <li>The device pairs and is ready for employees to clock in/out.</li>
            </ol>
          </div>
          <div className="kiosk-code-warn">
            For security, this code can be used once and won't be shown again. Generate a new one if it expires.
          </div>
        </div>
        <div className="kiosk-modal-footer">
          <button type="button" className="settings-btn settings-btn-secondary" onClick={copy} disabled={expired}>
            Copy code
          </button>
          <button type="button" className="settings-btn settings-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
    </Modal>
  );
};

export default PairingCodeModal;
